import React from 'react'

import { Icon } from 'src/chrome'
import {
  ContactFragment,
  getPaginatedContacts,
  getPaginatedGroups,
  GroupFragment,
} from 'src/graphql'

import { SearchFilter, SearchFilterType } from './types'
import TextInput from './lib/TextInput'
import ListFilterInput from './lib/ListFilterInput'
import DateRangeInput from './lib/DateRangeInput'
import { useState } from 'src/hooks'
import { Memoized } from 'src/hooks/dependencies'

import styles from './filteredSearchBar.module.scss'

const filterCountStyles = {
  right: -5,
  top: 10,
  position: 'absolute',
} as const

interface Props {
  filters: SearchFilter[]
  setFilters: Memoized<(filters: SearchFilter[]) => void>
  setTerms: Memoized<(terms: string) => void>
  className?: string
  inputClassName?: string
  style?: React.CSSProperties
  autocomplete?: 'on' | 'off'
}

const FilteredSearchBar: React.FC<Props> = props => {
  const {
    filters,
    className,
    inputClassName,
    style,
    setTerms,
    setFilters,
    autocomplete,
  } = props

  const [editedFilter, setEditedFilter] = useState<SearchFilter>()

  const clearAll = () => {
    setEditedFilter(undefined)
    setTerms('')
    setFilters(
      filters.map(filter => ({
        ...filter,
        value: undefined,
      })),
    )
  }

  const updateFilter = <T extends SearchFilter>(
    filter: T,
    update: Partial<T>,
  ) => {
    const index = filters.findIndex(x => x === filter)
    if (index < 0) {
      throw new Error(`filter not found: ${filter.name}`)
    }
    setFilters(
      filters.filter(x => x !== filter).concat([{ ...filter, ...update }]),
    )
  }

  const deleteFilter = (target: SearchFilter) =>
    updateFilter(target, { value: undefined })

  const definedFilters = filters.filter(filter => filter.value !== undefined)

  return (
    <div className={`${styles.searchField} ${className}`} style={style}>
      <div
        className={
          inputClassName ? `${styles.search} ${inputClassName}` : styles.search
        }
      >
        {definedFilters.length ? (
          <span className={styles.filterIcon}>
            <Icon icon={'FUNNEL'} size={18} color={'#ff5689'} />
            <span style={filterCountStyles}>{definedFilters.length}</span>
          </span>
        ) : (
          <span className={styles.searchIcon}>
            <Icon icon={'SEARCH'} size={18} color={'#ff5689'} />
          </span>
        )}

        {(() => {
          if (editedFilter) {
            switch (editedFilter.type) {
              case SearchFilterType.CONTACT:
                return (
                  <ListFilterInput<ContactFragment>
                    deleteFilter={deleteFilter}
                    filterName={editedFilter.name}
                    filters={filters}
                    onClear={clearAll}
                    onCancel={() => setEditedFilter(undefined)}
                    getKey={item => item.id}
                    getLabel={item => `${item.firstName} ${item.lastName}`}
                    getQuery={search =>
                      getPaginatedContacts({ search, page: 1 })
                    }
                    onSave={item => {
                      updateFilter(editedFilter, {
                        value: (editedFilter.value ?? []).concat([item]),
                      })
                      setEditedFilter(undefined)
                    }}
                  />
                )
              case SearchFilterType.GROUP:
                return (
                  <ListFilterInput<GroupFragment>
                    deleteFilter={deleteFilter}
                    filterName={editedFilter.name}
                    filters={filters}
                    onClear={clearAll}
                    onCancel={() => setEditedFilter(undefined)}
                    getKey={item => item.id}
                    getLabel={item => `${item.name}`}
                    getQuery={search => getPaginatedGroups({ search, page: 1 })}
                    onSave={item => {
                      updateFilter(editedFilter, {
                        value: (editedFilter.value ?? []).concat([item]),
                      })
                      setEditedFilter(undefined)
                    }}
                    autocomplete={'off'}
                  />
                )

              case SearchFilterType.DATERANGE:
                return (
                  <DateRangeInput
                    deleteFilter={deleteFilter}
                    filter={editedFilter}
                    filters={filters}
                    onClear={clearAll}
                    updateFilter={updateFilter}
                    onClose={() => setEditedFilter(undefined)}
                  />
                )
            }

            return null
          } else {
            return (
              <TextInput
                filters={filters}
                deleteFilter={deleteFilter}
                onClear={clearAll}
                setEditedFilter={setEditedFilter}
                updateFilter={updateFilter}
                onSearch={terms => setTerms(terms)}
                autocomplete={autocomplete}
              />
            )
          }
        })()}
      </div>
    </div>
  )
}

export default FilteredSearchBar
